import { IncomingHttpHeaders } from 'http';
import {
  JobListResponse,
  JobLocationResponse,
} from '@doda/api-client/aspida/@types';
import { PLP_COMMA_PAGE_LIST } from '@doda/common/src/constants/seo/plpCommaPageList';
import MasterCacheModel from '../models/MasterCacheModel';
import { META_TAG_SETTING } from '../constants/metaTag';
import { AppQueryParams } from '../constants/queryParams';
import {
  COMMA,
  COMMA_N,
  COMPANY_DODA_DES_NM,
  COMPANY_DODA_PR_DES_NM,
  COMPANY_DODA_JD_TITLE_NM,
  COMPANY_DODA_PR_TITLE_NM,
  DES_NM_2PAGE,
  DES_NM_DETAIL_2PAGE,
  DES_NM_DODA,
  DES_NM_DODA_DETAIL,
  DES_NM_DODA_DETAIL_WITH_COUNT,
  DODA_SEARCH_RESULT_LEFTPANEL_AR,
  DODA_SEARCH_RESULT_LEFTPANEL_IND,
  DODA_SEARCH_RESULT_LEFTPANEL_NO,
  DODA_SEARCH_RESULT_LEFTPANEL_OC,
  GREATER,
  KEY_NM,
  KEY_PR_NM,
  KEY_NM_2PAGE,
  KEY_NM_DETAIL,
  KEY_NM_DETAIL_2PAGE,
  KEY_NM_DETAIL_MASTER_2PAGE,
  LEFT_KAKO,
  LESSER,
  NEAR_AREA_TEXT_NEAR,
  PLUS,
  RIGHT_KAKO,
  SEPARATED_FULL_COLON,
  SIGN_FULLSIZE_KAKO_ED,
  SIGN_FULLSIZE_KAKO_ST,
  SPACE,
  STR_ETC,
  STR_NO,
  SURASHU_N,
  TITLE_NM_DODA,
  TITLE_NM_DODA_DETAIL,
  TITLE_NM_DODA_DETAIL_WITH_COUNT,
  TITLE_NM_PAGE,
  WORK_LOCATION,
  WRRST_FLG_SET,
  PATTERN_PAGE,
} from '../constants/seo';
import { getSearchMetaTag } from '../services/getMetaTagAPI';
import { existPropertyNumbers } from '../utils/objectUtil';
import { ownerOp, sortOp } from '../utils/opUtil';
import {
  adaptUrl,
  getEmployeesItemName,
  getEstablishmentItemName,
  gethaItemName,
  getOpName,
  getOpNames,
  onlyOneKeyExistsInKodawari,
  shouldEditArText,
  shouldEditIndText,
  shouldEditOcText,
  valueExistsInOp,
  haSelectCheck,
  isTwoValueInMap,
  isTwoParamPreK,
  isTwoParamOpK,
  isTwoParamHaK,
  isTwoParamNeK,
  isTwoParamEsK,
  getPlpCommaSummaryPageIndex,
} from '../utils/seoUtil';
import { formatString } from '../utils/stringUtil';
import { getSearchScreenKind } from './seo';
import { makeStaticUrl } from './staticUrl';

/**
 *詳細オプション
 */
export interface DetailOption {
  corporateName: string;
  occupationName: string;
  locations: JobLocationResponse[] | null;
  summaries: string[] | null;
  wcnsId: string;
  htmlExists: boolean;
  isPriorityDisplay: boolean;
}

/**
 * metaタグデータ
 */
export interface MetaText {
  title?: string;
  keywords?: string;
  description?: string;
  robots?: string | null;
}

/**
 * 求人詳細画面のmetaデータを取得
 * @param query クエリ
 * @param path 画面パス
 * @param param.corporateName 会社名
 * @param param.occupationName 職種名
 * @param param.locations ロケーション一覧
 * @param param.summaries 求人要約
 * @param param.wcnsId 原稿種別
 * @param param.htmlExists PR画像フラグ
 * @param param.isPriorityDisplay PR画像を優先して表示
 * @returns metaデータ
 */
export const getMetaTextOfJobSearchDetail = (
  query: AppQueryParams,
  path: string,
  {
    // job.corporateName
    corporateName,
    // job?.occupationName
    occupationName,
    // locations
    locations,
    // summaries
    summaries,
    // job.wcnsId
    wcnsId,
    htmlExists,
    isPriorityDisplay,
  }: DetailOption
): MetaText => {
  const metaInfo = findMetaTagInfo(query, path);
  if (metaInfo) {
    const { settingFlg } = metaInfo;
    if (WRRST_FLG_SET === settingFlg) {
      let title = '';
      if (summaries && query.tab === 'pr') {
        // Pick Up!の場合、職種名は不要
        title = corporateName;
        // Pick Up!の場合、title先頭に求人要約1を追加
        title = concatString(summaries[0], title, SURASHU_N);
      } else {
        title = concatString(corporateName, occupationName, SURASHU_N);
      }

      if (locations?.length) {
        // 文字列生成createJobLocationsStr
        const locationStr = createLocationStr(locations);
        title = concatString(title, locationStr, SURASHU_N);
      }

      // 求人詳細画面のtitle タグを取得する
      const eoption = {
        tab: query.tab ?? '',
        message: query.msg ?? '',
        wcnsId: wcnsId ?? '',
        htmlExists: htmlExists ?? false,
        isPriorityDisplay: isPriorityDisplay ?? false,
      };
      title += createTitleByManuscript(eoption);

      // 求人詳細画面のdescription タグを取得
      let description = '';
      if (summaries && query.tab === 'pr') {
        // Pick Up!の場合、職種名は不要
        description = corporateName;
        // Pick Up!の場合、description先頭に求人要約1を追加
        description = concatString(description, COMPANY_DODA_PR_DES_NM, STR_NO);
        description = concatString(summaries[0], description, SURASHU_N);
      } else {
        description = concatString(corporateName, occupationName, SURASHU_N);
        description = concatString(description, COMPANY_DODA_DES_NM, STR_NO);
      }

      // MetaKey = 企業名,新着求人用職種名,
      // 未使用のため削除↓
      // 職種大分類１,職種中分類１,
      // 職種大分類２,職種中分類２,固定文言
      let keywords = '';
      if (summaries && query.tab === 'pr') {
        // Pick Up!の場合、職種名は不要
        keywords = corporateName;
        // Pick Up!の場合、keywordsの先頭に求人要約1を追加
        keywords = concatString(keywords, KEY_PR_NM, COMMA);
        keywords = concatString(summaries[0], keywords, COMMA);
      } else {
        keywords = concatString(corporateName, occupationName, COMMA);
        keywords = concatString(keywords, KEY_NM, COMMA);
      }

      return {
        title,
        description,
        keywords,
      };
    }
    return {
      title: metaInfo.title ?? '',
      description: metaInfo.description ?? '',
      keywords: metaInfo.keywords ?? '',
    };
  }
  return {
    title: '',
    keywords: '',
    description: '',
  };
};

/**
 * 原稿種別からtitleを生成
 * @param param.wcnsId 原稿種別
 * @param param.htmlExists PR画像フラグ
 * @param param.isPriorityDisplay PR画像を優先して表示
 * @param param.tab 求人詳細用のタブ情報
 * @param param.message 求人詳細用のメッセージ情報
 * @returns title
 */
export const createTitleByManuscript = ({
  wcnsId,
  htmlExists,
  isPriorityDisplay,
  tab,
  message,
}) => {
  let title = '';
  if (wcnsId !== 'E') {
    // E原稿でない
    // Title = <$社名$>／<$新着職種$>の求人情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_JD_TITLE_NM;
  } else if (!htmlExists) {
    // E原稿かつ、HTMLデータが存在しない
    // Title = <$社名$>／<$新着職種$>の求人情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_JD_TITLE_NM;
  } else if (tab === 'pr') {
    // E原稿かつ、URLのtabパラメータで 求人PR の指定がある場合
    // Title = <$社名$>／<$新着職種$>の求人Pick up!情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_PR_TITLE_NM;
  } else if (tab === 'jd') {
    // E原稿かつ、URLのtabパラメータで 募集要項 の指定がある場合
    // Title = <$社名$>／<$新着職種$>の求人情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_JD_TITLE_NM;
  } else if (message === '1') {
    // E原稿かつ、URLのmessageパラメータで 求人PR の指定がある場合
    // Title = <$社名$>／<$新着職種$>の求人PR － 転職ならDODA（デューダ）
    title = COMPANY_DODA_PR_TITLE_NM;
  } else if (message === '0') {
    // E原稿かつ、URLのtabパラメータで 募集要項 の指定がある場合
    // Title = <$社名$>／<$新着職種$>の求人情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_JD_TITLE_NM;
  } else if (!isPriorityDisplay) {
    // E原稿かつ、tabパラメータ指定なし。かつ、初期表示区分が求人PR
    // Title = <$社名$>／<$新着職種$>の求人PR － 転職ならDODA（デューダ）
    title = COMPANY_DODA_PR_TITLE_NM;
  } else if (isPriorityDisplay) {
    // E原稿かつ、tabパラメータ指定なし。かつ、初期表示区分が募集要項
    // Title = <$社名$>／<$新着職種$>の求人情報 － 転職ならDODA（デューダ）
    title = COMPANY_DODA_JD_TITLE_NM;
  }
  return title;
};

/**
 * 勤務地文字列の生成
 * @param locations ロケーション一覧
 * @returns 勤務地文字列
 */
export const createLocationStr = (locations: JobLocationResponse[]) => {
  const nameList: string[] = [];
  let isMoreThanFour = false;
  // eslint-disable-next-line no-plusplus
  for (let cnt = 0; cnt < locations.length; cnt++) {
    // eslint-disable-next-line eqeqeq
    if (cnt == 3) {
      // 3件追加で終了
      isMoreThanFour = true;
      break;
    }
    const item = locations[cnt];
    if (item.name) nameList.push(item.name);
  }
  let result = WORK_LOCATION + SEPARATED_FULL_COLON + nameList.join(COMMA_N);
  if (isMoreThanFour) {
    result = result + SPACE + STR_ETC;
  }
  return result;
};

/**
 * メタタグ定義から使用する定義を取得します。
 * @param {AppQueryParams} query クエリマップ
 * @param {string} path urlのpath
 * @returns null | meta情報オブジェクト
 */
export const findMetaTagInfo = (query: AppQueryParams, path: string) => {
  const staticUrl = makeStaticUrl(query, path);

  const urlList = META_TAG_SETTING.map((item) => item.url);

  const rec = adaptUrl(staticUrl, urlList);

  if (rec !== null) {
    return META_TAG_SETTING[rec];
  }
  return null;
};

/**
 * 文字列を連結
 * @param {string} baseStr 連結元の文字列
 * @param {string} combinedStr 後ろに連結する文字列
 * @param {string} separator 区切り文字
 * @returns 連結後の文字列
 */
export const concatString = (
  baseStr: string,
  combinedStr: string,
  separator: string
) => {
  let resultStr = baseStr;
  if (combinedStr) {
    if (baseStr) {
      resultStr += separator;
    }
    resultStr += combinedStr;
  }

  return resultStr;
};

/**
 * 検索結果一覧 メタ情報の生成
 * @param jobList 求人一覧
 * @param page ページ番号
 * @param jobCountPerPage 1ページに表示する最大求人件数
 * @returns メタ情報
 */
export const createMetaDataOfJobSearchList = (
  jobList: JobListResponse,
  page: number,
  jobCountPerPage: number
) => {
  const totalJobCount = jobList.metaData?.totalCount ?? 0;
  const currentPage = page;
  const startNo = jobCountPerPage * (currentPage - 1) + 1;
  const endNo =
    jobCountPerPage * currentPage < totalJobCount
      ? jobCountPerPage * currentPage
      : totalJobCount;
  const option = {
    jobCount: String(totalJobCount),
    startNo: String(startNo),
    endNo: String(endNo),
  };

  return option;
};

interface ListOption {
  jobCount: string;
  startNo: string;
  endNo: string;
}

/**
 * 検索結果一覧画面のmetaデータを取得
 * @param query クエリ
 * @param path 画面パス
 * @param option メタ情報
 * @param canonical canonicalUrl
 * @param headers HTTPヘッダー
 * @returns metaデータ
 */
export const getMetaTextOfJobSearchList = async (
  query: AppQueryParams,
  path: string,
  option: ListOption,
  canonical: string | undefined,
  headers?: IncomingHttpHeaders
): Promise<MetaText> => {
  const metaInfo = findMetaTagInfo(query, path);
  if (metaInfo) {
    let titleMetaMaster = {
      title: '',
      keyword: '',
      description: '',
      descriptionNextPage: '',
    };

    if (canonical) {
      const canonicalForSearch = canonical.replace(PATTERN_PAGE, '').slice(1);
      titleMetaMaster = await getSearchMetaTag(canonicalForSearch, headers);
    }
    const masterDataTitle = titleMetaMaster.title
      ? createMasterDataTitle(titleMetaMaster.title, query)
      : '';
    const masterDataKeywords = titleMetaMaster.keyword
      ? createMasterDataKeyword(titleMetaMaster.keyword, query, option)
      : '';
    const masterDataDescription = titleMetaMaster.description
      ? createMasterDataDescription(
          titleMetaMaster.description,
          titleMetaMaster.descriptionNextPage,
          query,
          option
        )
      : '';

    const { settingFlg } = metaInfo;
    if (WRRST_FLG_SET === settingFlg) {
      const { ha, ne, es, k } = query;
      const op = ownerOp(query.op);
      let kodawariItemName = '';
      // こだわり、希望年収、従業員数、設立、キーワードいずれか一つが設定されていればこだわり条件を設定する
      if (onlyOneKeyExistsInKodawari({ op, ha, ne, es })) {
        if (op && valueExistsInOp(op, 1)) {
          kodawariItemName = getOpName(op);
        } else if (op && valueExistsInOp(op, 2)) {
          kodawariItemName = getOpNames(op).join(',');
        } else if (ha && haSelectCheck(ha)) {
          kodawariItemName = gethaItemName(ha[0]);
        } else if (ne && ne.length === 1) {
          kodawariItemName = getEmployeesItemName(ne[0]);
        } else if (es && es.length === 1) {
          kodawariItemName = getEstablishmentItemName(es[0]);
        }
      } else if (op && ha && isTwoValueInMap(query)) {
        kodawariItemName = `${getOpNames(op)},${gethaItemName(ha[0])}`;
      } else if (k && isTwoParamPreK(query)) {
        kodawariItemName = k;
      }

      const screenKind = getSearchScreenKind(
        path,
        { ...query, preBtn: query.nearArea ? '1' : query.preBtn },
        {}
      );

      let customKeyword = '';
      let customDescription = '';

      switch (screenKind) {
        case DODA_SEARCH_RESULT_LEFTPANEL_OC:
          [customKeyword, customDescription] =
            getMetaTextOfOcKindInJobSearchList(query, kodawariItemName);
          break;

        case DODA_SEARCH_RESULT_LEFTPANEL_AR:
          [customKeyword, customDescription] =
            getMetaTextOfArKindInJobSearchList(query, kodawariItemName);
          break;

        case DODA_SEARCH_RESULT_LEFTPANEL_IND:
          [customKeyword, customDescription] =
            getMetaTextOfIndKindInJobSearchList(query, kodawariItemName);
          break;

        case DODA_SEARCH_RESULT_LEFTPANEL_NO:
          [customKeyword, customDescription] =
            getMetaTextOfNoKindInJobSearchList(query, kodawariItemName);
          break;

        default:
          break;
      }

      if (!customKeyword || !customDescription) {
        // PLPまとめページの場合
        const index = getPlpCommaSummaryPageIndex(canonical);
        if (index > -1) {
          customKeyword = PLP_COMMA_PAGE_LIST[index].commaPageName;
          customDescription = PLP_COMMA_PAGE_LIST[index].commaPageName;
        }
      }

      if (customKeyword && customDescription) {
        const title =
          masterDataTitle || createCustomTitle(query, customKeyword, true);
        const keywords =
          masterDataKeywords ||
          createCustomKeyword(query, customKeyword, option);
        const description =
          masterDataDescription ||
          createCustomDescription(query, customDescription, option);

        return {
          title,
          keywords,
          description,
        };
      }
    }
    // 固定文章
    const title = createFixTitle(query);
    const keywords = createFixKeyword(query, option);
    const description = createFixDescription(query, option);
    return {
      title,
      keywords,
      description,
    };
  }
  return {
    title: '',
    keywords: '',
    description: '',
  };
};

const createFixTitle = (query: AppQueryParams) => {
  const { page } = query;
  if (page) {
    return formatString(TITLE_NM_DODA, [page + TITLE_NM_PAGE]);
  }
  return formatString(TITLE_NM_DODA, ['']);
};
const createFixKeyword = (query: AppQueryParams, option: ListOption) => {
  const { page } = query;
  const { startNo, endNo } = option;
  if (page) {
    return formatString(KEY_NM_2PAGE, [page, startNo, endNo]);
  }
  return KEY_NM;
};
const createFixDescription = (query: AppQueryParams, option: ListOption) => {
  const { page } = query;
  const { startNo, endNo } = option;
  if (page) {
    return formatString(DES_NM_2PAGE, [
      page,
      startNo,
      endNo,
      page,
      startNo,
      endNo,
    ]);
  }
  return DES_NM_DODA;
};
/**
 * APIレスポンスからdescriptionを生成
 * @param masterText description
 * @param masterText2 descriptionNextPage
 * @param query クエリ
 * @param option メタ情報
 * @returns description
 */
export const createMasterDataDescription = (
  masterText: string,
  masterText2: string,
  query: AppQueryParams,
  option: ListOption
) => {
  const { page } = query;
  const { jobCount, startNo, endNo } = option;
  if (page) {
    return formatString(DES_NM_DETAIL_2PAGE, [
      page,
      startNo,
      endNo,
      masterText2,
      page,
      startNo,
      endNo,
    ]);
  }
  if (jobCount) {
    return formatString(masterText, [jobCount]);
  }
  return formatString(DES_NM_DODA_DETAIL, [masterText2]);
};
/**
 * APIレスポンスからkeywordを生成
 * @param masterText keyword
 * @param query クエリ
 * @param option メタ情報
 * @returns keyword
 */
export const createMasterDataKeyword = (
  masterText: string,
  query: AppQueryParams,
  option: ListOption
) => {
  const { page } = query;
  const { startNo, endNo } = option;
  if (page) {
    return formatString(KEY_NM_DETAIL_MASTER_2PAGE, [
      page,
      startNo,
      endNo,
      masterText,
    ]);
  }
  return masterText;
};
/**
 * APIレスポンスからtitleを生成
 * @param masterText title
 * @param query クエリ
 * @returns title
 */
export const createMasterDataTitle = (
  masterText: string,
  query: AppQueryParams
) => {
  const { page } = query;
  if (page) {
    return page + TITLE_NM_PAGE + masterText;
  }
  return masterText;
};
/**
 * descriptionを生成
 * @param query クエリ
 * @param text customDescription
 * @param option メタ情報
 * @returns description
 */
export const createCustomDescription = (
  query: AppQueryParams,
  text: string,
  option: ListOption
) => {
  const { page } = query;
  const { jobCount, startNo, endNo } = option;
  if (page) {
    return formatString(DES_NM_DETAIL_2PAGE, [
      page,
      startNo,
      endNo,
      text,
      page,
      startNo,
      endNo,
    ]);
  }
  if (jobCount) {
    return formatString(DES_NM_DODA_DETAIL_WITH_COUNT, [text, jobCount]);
  }
  return formatString(DES_NM_DODA_DETAIL, [text]);
};
/**
 * keywordを生成
 * @param query クエリ
 * @param text customKeyword
 * @param option メタ情報
 * @returns keyword
 */
export const createCustomKeyword = (
  query: AppQueryParams,
  text: string,
  option: ListOption
) => {
  const { page } = query;
  const { startNo, endNo } = option;
  if (page) {
    return formatString(KEY_NM_DETAIL_2PAGE, [page, startNo, endNo, text]);
  }
  return formatString(KEY_NM_DETAIL, [text]);
};
/**
 * titleを生成
 * @param query クエリ
 * @param text customKeyword
 * @param isReplacedComma カンマ区切り判定フラグ
 * @returns title
 */
export const createCustomTitle = (
  query: AppQueryParams,
  text: string,
  isReplacedComma: boolean = false
) => {
  const { page } = query;
  // 【北海道】営業職＜正社員＞
  let textList = text.split(COMMA);
  // eslint-disable-next-line no-shadow
  textList = textList.map((text, idx) => {
    if (text) {
      if (idx === 0) return LEFT_KAKO + text + RIGHT_KAKO;
      if (idx === 1) return text;
      if (idx === 2) return LESSER + text + GREATER;
    }
    return '';
  });
  if (page) {
    if (isReplacedComma) {
      return formatString(TITLE_NM_DODA_DETAIL_WITH_COUNT, [
        page + TITLE_NM_PAGE,
        textList[0],
        textList[1] ?? '',
        textList[2] ?? '',
      ]);
    }
    return formatString(TITLE_NM_DODA_DETAIL, [page + TITLE_NM_PAGE, text]);
  }
  if (isReplacedComma) {
    return formatString(TITLE_NM_DODA_DETAIL_WITH_COUNT, [
      '',
      textList[0],
      textList[1] ?? '',
      textList[2] ?? '',
    ]);
  }
  return formatString(TITLE_NM_DODA_DETAIL, ['', text]);
};

/**
 * 通常パターンの検索結果一覧画面のmetaデータを取得
 * @param query クエリ
 * @param kodawari こだわり条件名
 * @returns keyword,description
 */
export const getMetaTextOfNoKindInJobSearchList = (
  query: AppQueryParams,
  kodawari: string
) => {
  const { ind, oc, ar, pr, ci, wa, rt, st, k, ha, ne, es } = query;
  const op = sortOp(ownerOp(query.op));
  const condition = { ind, oc, ar, pr, ci, wa, rt, st, k, op, ha, ne, es };
  const propertySize = existPropertyNumbers(condition);

  let keyword = '';
  let description = '';
  if (propertySize === 1) {
    if (op && kodawari) {
      keyword = kodawari;
      description = kodawari;
    }
    if (k && k.indexOf(PLUS) === -1) {
      keyword = k;
      description = k;
    }
    if (ha && kodawari) {
      keyword = kodawari;
      description = kodawari;
    }
    if (ne && kodawari) {
      keyword = kodawari;
      description = kodawari;
    }
    if (es && kodawari) {
      keyword = kodawari;
      description = kodawari;
    }
    if (valueExistsInOp(op, 2)) {
      // こだわり条件２つ選択
      // selected two op conditions
      const nameList = getOpNames(op ?? []);
      keyword = concatString(nameList[0], nameList[1], COMMA);
      description = concatString(nameList[0], nameList[1], COMMA_N);
    } else if (valueExistsInOp(op, 3)) {
      // こだわり条件３つ選択
      // selected three op conditions
      const nameList = getOpNames(op ?? []);
      keyword = concatString(
        concatString(nameList[0], nameList[1], COMMA),
        nameList[2],
        COMMA
      );
      description = concatString(
        concatString(nameList[0], nameList[1], COMMA_N),
        nameList[2],
        COMMA_N
      );
    }
  } else if (propertySize === 2) {
    if (valueExistsInOp(op, 1) && ha) {
      // こだわり条件１つと年収選択
      // selected one op and ha conditions
      const opName = getOpName(op ?? []);
      const haLowerName = gethaItemName(ha[0]);
      if (haSelectCheck(ha)) {
        keyword = concatString(opName, haLowerName, COMMA);
        description = concatString(opName, haLowerName, COMMA_N);
      }
    } else if (valueExistsInOp(op, 2) && ha) {
      // こだわり条件2つと年収選択
      // selected one op and ha conditions
      const opNames = getOpNames(op ?? []);
      const haLowerName = gethaItemName(ha[0]);
      if (haSelectCheck(ha)) {
        keyword = concatString(
          concatString(opNames[0], opNames[1], COMMA),
          haLowerName,
          COMMA
        );
        description = concatString(
          concatString(opNames[0], opNames[1], COMMA_N),
          haLowerName,
          COMMA_N
        );
      }
    } else if (k) {
      if (
        kodawari &&
        (isTwoParamOpK(query) ||
          isTwoParamHaK(query) ||
          isTwoParamNeK(query) ||
          isTwoParamEsK(query))
      ) {
        // こだわり1軸 × フリーワード
        keyword = concatString(kodawari, k, COMMA);
        description = concatString(kodawari, k, COMMA_N);
      }
    }
  }
  return [keyword, description];
};
/**
 * 業種ドリルダウンの検索結果一覧画面のmetaデータを取得
 * @param query クエリ
 * @param kodawari こだわり条件名
 * @returns keyword,description
 */
export const getMetaTextOfIndKindInJobSearchList = (
  query: AppQueryParams,
  kodawari: string
) => {
  const shouldEdit = shouldEditIndText(query, kodawari);
  let keyword = '';
  let description = '';

  if (shouldEdit) {
    const { ar, pr, ci, wa, oc, ind } = query;
    if (ind) {
      const indId = ind[0];
      if (indId) {
        const indName = getIndNameFromMaster(indId);
        keyword = indName;
        description = indName;
        if (ar) {
          const value = ar[0];
          const arName = getArNameFromMaster(value);
          if (arName) {
            keyword = concatString(keyword, arName, COMMA);
            description = concatString(description, arName, COMMA_N);
          }
        }
        if (pr) {
          const value = pr[0];
          const prName = getPrNameFromMaster(value);
          if (prName) {
            keyword = concatString(keyword, prName, COMMA);
            description = concatString(description, prName, COMMA_N);
          }
        }
        if (ci) {
          const city = MasterCacheModel.getCityWithPref(ci[0]);
          const ciName = city ? createArText(city.name, city.prefName) : '';
          if (ciName) {
            keyword = concatString(keyword, ciName, COMMA);
            description = concatString(description, ciName, COMMA_N);
          }
        }
        if (wa) {
          const ward = MasterCacheModel.getWardWithPref(wa[0]);
          const waName = ward ? createArText(ward.name, ward.prefName) : '';
          if (waName) {
            keyword = concatString(keyword, waName, COMMA);
            description = concatString(description, waName, COMMA_N);
          }
        }
        if (oc) {
          const value = oc[0];
          const ocName = getOcNameFromMaster(value);
          if (ocName) {
            keyword = concatString(keyword, ocName, COMMA);
            description = concatString(description, ocName, COMMA_N);
          }
        }
        if (kodawari) {
          keyword = concatString(keyword, kodawari, COMMA);
          description = concatString(
            description,
            kodawari.split(',').join('、'),
            COMMA_N
          );
        }
      }
    }
  }
  return [keyword, description];
};

/**
 * 勤務地ドリルダウンの検索結果一覧画面のmetaデータを取得
 * @param query クエリ
 * @param kodawari こだわり条件名
 * @returns keyword,description
 */
export const getMetaTextOfArKindInJobSearchList = (
  query: AppQueryParams,
  kodawari: string
) => {
  const shouldEdit = shouldEditArText(query, kodawari);
  let keyword = '';
  let description = '';

  const { ar, pr, ci, wa, st, rt, oc, nearArea, baseArea } = query;
  if (shouldEdit || nearArea) {
    let name = '';
    if (ar) {
      name = getArNameFromMaster(ar[0]);
    } else if (pr) {
      name = getPrNameFromMaster(baseArea || pr[0]);
    } else if (ci) {
      // ci(pr)
      const city = MasterCacheModel.getCityWithPref(baseArea || ci[0]);
      if (city) {
        name = createArText(city.name, city.prefName);
      }
    } else if (wa) {
      // wa(pr)
      const ward = MasterCacheModel.getWardWithPref(baseArea || wa[0]);
      if (ward) {
        name = createArText(ward.name, ward.prefName);
      }
    } else if (st) {
      // st(pr)
      const station = MasterCacheModel.getStationWithPref(baseArea || st[0]);
      if (station) {
        name = createArText(station.name, station.prefName);
      }
    } else if (rt) {
      //  路線名-鉄道会社名
      const route = MasterCacheModel.getRouteWithCompany(rt[0]);
      if (route) {
        name = `${route.name}-${route.companyName}`;
      }
    }
    if (name) {
      if (nearArea && !(ar || rt)) {
        // 近隣検索の場合、「近隣」の文言を追加
        name += NEAR_AREA_TEXT_NEAR;
      }
      [keyword, description] = createOcTextOfAr(name, kodawari, oc);
    }
  }
  return [keyword, description];
};

/**
 * 職種ドリルダウンの検索結果一覧画面のmetaデータを取得
 * @param query クエリ
 * @param kodawari こだわり条件名
 * @returns keyword,description
 */
export const getMetaTextOfOcKindInJobSearchList = (
  query: AppQueryParams,
  kodawari: string
) => {
  const shouldEdit = query.oc && shouldEditOcText(query, kodawari);

  let description = '';
  let keyword = '';
  if (shouldEdit) {
    // Keyword_CommaN = 職種名、こだわり
    description = createOcOpText(
      query.oc ?? [],
      kodawari.split(',').join('、'),
      COMMA_N
    );

    // Keyword_Comma = 職種名,こだわり
    keyword = createOcOpText(query.oc ?? [], kodawari);
  }
  return [keyword, description];
};

const createArText = (ar: string, pref: string) => {
  if (ar && pref) {
    // eslint-disable-next-line no-useless-escape
    const onlyAr = ar.replace(/\(([^\)]*)\)/, '');
    return onlyAr + SIGN_FULLSIZE_KAKO_ST + pref + SIGN_FULLSIZE_KAKO_ED;
  }
  return '';
};

/**
 * 職種テキストを生成
 * @param baseName マスタデータから生成したテキスト
 * @param kodawari こだわり条件名
 * @param oc 職種
 * @returns keyword,description
 */
export const createOcTextOfAr = (
  baseName: string,
  kodawari: string,
  oc: AppQueryParams['oc']
) => {
  let keyword = '';
  let description = '';
  if (oc && oc?.length > 0) {
    const ocOpText = createOcOpText(oc, kodawari);
    const ocOpDesText = createOcOpText(oc, kodawari, COMMA_N);
    keyword = concatString(baseName, ocOpText, COMMA);
    description = concatString(baseName, ocOpDesText, COMMA_N);
  } else {
    keyword = concatString(baseName, kodawari, COMMA);
    description = concatString(
      baseName,
      kodawari.split(',').join('、'),
      COMMA_N
    );
  }
  return [keyword, description];
};

/**
 * 業種コードに紐づく業種名をマスタデータから取得
 * @param ind 業種コード
 * @returns 業種名
 */
export const getIndNameFromMaster = (ind: string) => {
  let value = '';
  if (ind.match(/(L|S)$/)) {
    value = ind.slice(0, -1);
  } else {
    value = ind;
  }
  return MasterCacheModel.findIndustry(value)?.name ?? '';
};
/**
 * 職種コードに紐づく職種名をマスタデータから取得
 * @param oc 職種コード
 * @returns 職種名
 */
export const getOcNameFromMaster = (oc: string) => {
  let value = '';
  if (oc.match(/(L|M|S)$/)) {
    value = oc.slice(0, -1);
  } else {
    value = oc;
  }
  return MasterCacheModel.findOccupation(value)?.searchJobTypeName ?? '';
};
/**
 * エリアコードに紐づくエリア名をマスタデータから取得
 * @param ar エリアコード
 * @returns エリア名
 */
export const getArNameFromMaster = (ar: string) => {
  return MasterCacheModel.findArea(ar)?.name ?? '';
};
/**
 * 都道府県コードに紐づく都道府県名をマスタデータから取得
 * @param pr 都道府県コード
 * @returns 都道府県名
 */
export const getPrNameFromMaster = (pr: string) => {
  return MasterCacheModel.findPrefecture(pr)?.name ?? '';
};
/**
 * 市区町村コードに紐づく市区町村名マスタデータから取得
 * @param ci 市区町村コード
 * @returns 市区町村名
 */
export const getCiNameFromMaster = (ci: string) => {
  return MasterCacheModel.findCity(ci)?.name ?? '';
};
/**
 * 政令指定都市コードに紐づく政令指定都市名をマスタデータから取得
 * @param wa 政令指定都市コード
 * @returns 政令指定都市名
 */
export const getWaNameFromMaster = (wa: string) => {
  return MasterCacheModel.findWard(wa)?.name ?? '';
};

/**
 * 職種名・こだわり条件テキストを生成
 * @param oc 職種
 * @param kodawari こだわり条件名
 * @param separator 区切り文字
 * @returns 職種名・こだわり条件テキスト
 */
export const createOcOpText = (
  oc: string[],
  kodawari: string,
  separator = COMMA
) => {
  let text = '';

  if (oc.length === 1) {
    const ocValue = oc[0];
    const name = getOcNameFromMaster(ocValue);
    text = concatString(name, kodawari, separator);
  }
  return text;
};
